"use client";

/**
 * MarkdownEditor — minimal Tiptap-backed text surface for Marp markdown drafts.
 *
 * Wave 1 contract (Marp migration, May 2026):
 *   - Stores/emits markdown as a plain string. Tiptap renders it as plaintext
 *     for now; Wave 2 will add prosemirror-markdown round-tripping so headings,
 *     lists, and fenced blocks survive the edit/serialize cycle.
 *   - `editor.getText()` is the canonical read path so paste-from-Marp doesn't
 *     get HTML-escaped on the way back out.
 *   - `immediatelyRender: false` is mandatory: apps/web is Next.js App Router
 *     (RSC streaming) and Tiptap 3 throws "SSR has been detected" if the
 *     editor instantiates during the first server-streamed render.
 *
 * Used by both apps/web (Next.js) and apps/client (Vite/Tauri) via `@ando/ui`.
 */

import CharacterCount from "@tiptap/extension-character-count";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { common, createLowlight } from "lowlight";
import * as React from "react";

import { cn } from "../lib/cn";

export interface MarkdownEditorProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly placeholder?: string;
  readonly editable?: boolean;
  readonly onSelectionChange?: (selectedText: string, from: number, to: number) => void;
  readonly className?: string;
}

const lowlight = createLowlight(common);

export function MarkdownEditor({
  value,
  onChange,
  placeholder = "Inizia a scrivere…",
  editable = true,
  onSelectionChange,
  className,
}: MarkdownEditorProps): React.JSX.Element {
  const onChangeRef = React.useRef(onChange);
  const onSelectionChangeRef = React.useRef(onSelectionChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
    onSelectionChangeRef.current = onSelectionChange;
  }, [onChange, onSelectionChange]);

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    content: value,
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Placeholder.configure({ placeholder }),
      CodeBlockLowlight.configure({ lowlight }),
      CharacterCount,
    ],
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm sm:prose-base max-w-none focus:outline-none",
          "min-h-[240px] px-4 py-3 font-text text-[color:hsl(var(--primary))]",
        ),
      },
    },
    onUpdate: ({ editor: instance }) => {
      onChangeRef.current(instance.getText());
    },
    onSelectionUpdate: ({ editor: instance }) => {
      const handler = onSelectionChangeRef.current;
      if (!handler) return;
      const { from, to, empty } = instance.state.selection;
      if (empty) {
        handler("", from, to);
        return;
      }
      handler(instance.state.doc.textBetween(from, to, "\n"), from, to);
    },
  });

  React.useEffect(() => {
    if (!editor) return;
    if (editor.getText() === value) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  React.useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  return (
    <div
      className={cn(
        "rounded-lg border border-[color:hsl(var(--border))] bg-[color:hsl(var(--background))]",
        "focus-within:border-[color:hsl(var(--accent))] focus-within:ring-2 focus-within:ring-[color:hsl(var(--accent)/0.25)]",
        "transition-colors",
        className,
      )}
    >
      <EditorContent editor={editor} />
    </div>
  );
}
